/**
 * UserMenu — bouton avatar dans la Topbar avec mini-dropdown.
 *
 * V0 minimaliste : avatar cliquable, panneau qui s'ouvre au-dessous, un seul
 * item "Se déconnecter". On évite les libs (Radix/Headless UI) tant qu'on
 * n'a qu'une action — ça reste 50 lignes lisibles.
 *
 * Comportement attendu :
 *  - clic avatar  → ouvre/ferme
 *  - clic dehors  → ferme
 *  - touche Esc   → ferme
 *  - logout       → appelle trpc.auth.logout, purge les caches locaux, va sur /login
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Download, LogOut, UserCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { purgerContexteSessionLocal } from "../../auth/purger-contexte-session.js";
import {
  BasculeSessionIncertitudeError,
  executerBasculeSessionExclusive,
  notifierBasculeSessionTerminee,
} from "../../auth/session-handoff-lock.js";
import { trpc } from "../../lib/trpc.js";
import { GuideInstallationIos } from "../../pwa/GuideInstallationIos.js";
import { useInstallationPwa } from "../../pwa/use-installation-pwa.js";

export interface UserMenuProps {
  utilisateur: {
    initiales: string;
    nomComplet: string;
    role: string;
  };
}

class OperationsHorsLigneEnAttenteError extends Error {
  constructor(public readonly nombre: number) {
    super(`${nombre} opération(s) hors ligne attendent encore leur envoi.`);
    this.name = "OperationsHorsLigneEnAttenteError";
  }
}

export function UserMenu({ utilisateur }: UserMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [erreurDeconnexion, setErreurDeconnexion] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Installation PWA : entrée de menu permanente (la bannière dashboard est
  // refusable, le menu reste le point d'accès de rattrapage).
  const { proposerInstallation, peutInstallerNativement, estIos, lancerInstallation } =
    useInstallationPwa();
  const [guideIosOuvert, setGuideIosOuvert] = useState(false);

  // Fermeture au clic dehors + touche Escape — patterns classiques pour
  // les menus déroulants. On ne s'attache QUE quand le menu est ouvert
  // pour ne pas écouter inutilement.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const logoutMutation = useMutation({
    mutationFn: () =>
      executerBasculeSessionExclusive(async () => {
        const offline = await import("../../offline/index.js");
        const nombreOperations = await offline.compterOperationsHorsLigneNonSynchronisees();
        if (nombreOperations > 0) {
          throw new OperationsHorsLigneEnAttenteError(nombreOperations);
        }

        // La purge n'est autorisée qu'après le contrôle atomique des outboxes.
        // Si le serveur accepte le logout mais que la purge échoue, conserver
        // le verrou jusqu'au TTL évite qu'un nouvel utilisateur entre dans un
        // stockage local dont l'isolation est incertaine.
        await trpc.auth.logout.mutate();
        try {
          await purgerContexteSessionLocal(queryClient);
        } catch (erreur) {
          throw new BasculeSessionIncertitudeError(erreur);
        }
      }),
    onMutate: () => setErreurDeconnexion(null),
    onSuccess: () => {
      notifierBasculeSessionTerminee();
      navigate("/login", { replace: true });
    },
    onError: (erreur) => {
      if (erreur instanceof OperationsHorsLigneEnAttenteError) {
        setErreurDeconnexion(
          `Synchronisez d’abord ${erreur.nombre} opération${erreur.nombre > 1 ? "s" : ""} hors ligne. Rien n’a été supprimé.`,
        );
        return;
      }
      if (erreur instanceof BasculeSessionIncertitudeError) {
        navigate("/login?recovered=logout", { replace: true });
        return;
      }
      // Une panne réseau avant confirmation du logout ne justifie aucune
      // suppression locale : la session reste affichée et l'utilisateur peut
      // réessayer sans perdre ses saisies.
      setErreurDeconnexion("Déconnexion impossible pour le moment. Réessayez.");
    },
  });

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="ui-btn-outline flex h-10 items-center gap-2.5 rounded-xl pl-2 pr-3"
        aria-label={`Compte ${utilisateur.nomComplet}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* Avatar 32px dans un bouton de 40px → ~4px de marge en haut/bas
            (au lieu de ~2px avec un avatar 36px) pour qu'il ne touche pas
            la bordure du rectangle. */}
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold"
          style={{
            background: "var(--color-aubergine-soft)",
            color: "var(--color-aubergine-deep)",
            border: "1px solid rgba(91, 58, 94, 0.18)",
          }}
          aria-hidden="true"
        >
          {utilisateur.initiales}
        </span>
        <div className="hidden text-left leading-tight lg:block">
          <div className="text-[13px] font-semibold">{utilisateur.nomComplet}</div>
          <div className="text-[11px] text-champagne-700">{utilisateur.role}</div>
        </div>
        <ChevronDown
          size={16}
          className={`text-champagne-700 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-20 w-56 overflow-hidden rounded-xl border border-champagne-900/10 bg-white shadow-lg"
        >
          {/* Bandeau identité — utile sur viewport étroit où nomComplet n'est
              pas affiché à côté de l'avatar. */}
          <div className="border-b border-champagne-900/10 px-4 py-3 leading-tight lg:hidden">
            <div className="text-[13px] font-semibold">{utilisateur.nomComplet}</div>
            <div className="text-[11px] text-champagne-700">{utilisateur.role}</div>
          </div>

          {/* Entrée unique « Mon profil » — la page index `/profile` regroupe
              toutes les sous-pages liées au compte utilisateur (Sécurité 2FA,
              Mes sessions actives, et à venir : infos perso, préférences,
              notifications). On évite ainsi de gonfler ce menu à chaque
              ajout. */}
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-[13px] text-champagne-900 transition-colors hover:bg-champagne-50"
          >
            <UserCircle size={16} className="text-champagne-700" aria-hidden="true" />
            Mon profil
          </Link>

          {proposerInstallation && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                if (peutInstallerNativement) {
                  void lancerInstallation();
                } else if (estIos) {
                  setGuideIosOuvert(true);
                }
              }}
              className="flex w-full items-center gap-2.5 border-t border-champagne-900/10 px-4 py-3 text-left text-[13px] text-champagne-900 transition-colors hover:bg-champagne-50"
            >
              <Download size={16} className="text-champagne-700" aria-hidden="true" />
              Installer l'application
            </button>
          )}

          {erreurDeconnexion && (
            <p
              role="alert"
              className="border-t border-red-200 bg-red-50 px-4 py-3 text-[12px] leading-relaxed text-red-800"
            >
              {erreurDeconnexion}
            </p>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            className="flex w-full items-center gap-2.5 border-t border-champagne-900/10 px-4 py-3 text-left text-[13px] text-champagne-900 transition-colors hover:bg-champagne-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <LogOut size={16} className="text-champagne-700" aria-hidden="true" />
            {logoutMutation.isPending ? "Déconnexion…" : "Se déconnecter"}
          </button>
        </div>
      )}

      {/* Guide iOS : rendu hors du dropdown (qui se ferme au clic). */}
      <GuideInstallationIos open={guideIosOuvert} onClose={() => setGuideIosOuvert(false)} />
    </div>
  );
}
